const MESSAGE_HISTORY_START = '<|im_start|>'
const MESSAGE_HISTORY_END = '<|im_end|>'
const MESSAGE_HISTORY_PREVIEW_MAX_LENGTH = 80
const MESSAGE_HISTORY_MAX_ENTRIES = 200
const MESSAGE_HISTORY_MAX_ENTRY_LENGTH = 64 * 1024
const MESSAGE_HISTORY_MAX_TOTAL_LENGTH = 256 * 1024

type PromptBodyMessageHistoryEntry = {
  role: string
  contentStart: number
  contentEnd: number
}

type PromptBodyMessageHistory = {
  entries: PromptBodyMessageHistoryEntry[]
  totalEntryCount: number
}

function startsWithMessageHistory(body: string): boolean {
  let start = 0

  while (start < body.length && /\s/.test(body[start] ?? '')) {
    start++
  }

  return body.startsWith(MESSAGE_HISTORY_START, start)
}

function parsePromptBodyMessageHistory(body: string): PromptBodyMessageHistory {
  const entries: PromptBodyMessageHistoryEntry[] = []
  let totalEntryCount = 0
  let cursor = 0

  while (cursor < body.length) {
    const entryStart = body.indexOf(MESSAGE_HISTORY_START, cursor)
    if (entryStart === -1) break

    const roleStart = entryStart + MESSAGE_HISTORY_START.length
    const entryEnd = body.indexOf(MESSAGE_HISTORY_END, roleStart)
    if (entryEnd === -1) break

    const roleEnd = body.indexOf('\n', roleStart)
    cursor = entryEnd + MESSAGE_HISTORY_END.length

    if (roleEnd === -1 || roleEnd === roleStart || roleEnd > entryEnd) continue

    let contentStart = roleEnd + 1
    let contentEnd = entryEnd

    while (contentStart < contentEnd && body[contentStart] === '\n') contentStart++
    while (contentEnd > contentStart && body[contentEnd - 1] === '\n') contentEnd--

    if (entries.length === MESSAGE_HISTORY_MAX_ENTRIES) entries.shift()

    entries.push({
      role: body.slice(roleStart, Math.min(roleEnd, roleStart + MESSAGE_HISTORY_PREVIEW_MAX_LENGTH)).trim(),
      contentStart,
      contentEnd,
    })
    totalEntryCount++
  }

  return {entries, totalEntryCount}
}

function getTruncationMarker(omittedLength: number): string {
  return `\n... ${omittedLength} characters omitted ...\n`
}

function truncateContent(content: string, maxLength: number): string {
  if (content.length <= maxLength) return content

  let omittedLength = content.length - maxLength
  let marker = getTruncationMarker(omittedLength)
  let visibleLength = Math.max(0, maxLength - marker.length)

  while (omittedLength !== content.length - visibleLength) {
    omittedLength = content.length - visibleLength
    marker = getTruncationMarker(omittedLength)
    visibleLength = Math.max(0, maxLength - marker.length)
  }

  if (visibleLength === 0) return marker.slice(0, maxLength)

  const headLength = Math.ceil(visibleLength * 0.75)
  return `${content.slice(0, headLength)}${marker}${content.slice(content.length - (visibleLength - headLength))}`
}

function getPerEntryLengthLimit(entries: PromptBodyMessageHistoryEntry[]): number {
  let minimum = 0
  let maximum = MESSAGE_HISTORY_MAX_ENTRY_LENGTH

  while (minimum < maximum) {
    const candidate = Math.ceil((minimum + maximum) / 2)
    const totalLength = entries.reduce(
      (sum, entry) => sum + Math.min(entry.contentEnd - entry.contentStart, candidate),
      0,
    )

    if (totalLength <= MESSAGE_HISTORY_MAX_TOTAL_LENGTH) {
      minimum = candidate
    } else {
      maximum = candidate - 1
    }
  }

  return minimum
}

function getMessagePreview(content: string): string {
  const firstNonEmptyLine = content
    .split('\n')
    .map(line => line.trim())
    .find(line => line.length > 0)

  if (!firstNonEmptyLine) return ''
  if (firstNonEmptyLine.length <= MESSAGE_HISTORY_PREVIEW_MAX_LENGTH) return firstNonEmptyLine

  return `${firstNonEmptyLine.slice(0, MESSAGE_HISTORY_PREVIEW_MAX_LENGTH - 3)}...`
}

export function logPromptBody(body: string): void {
  if (!startsWithMessageHistory(body)) {
    // eslint-disable-next-line no-console
    console.log('Prompt Body:', truncateContent(body, MESSAGE_HISTORY_MAX_TOTAL_LENGTH))
    return
  }

  const {entries, totalEntryCount} = parsePromptBodyMessageHistory(body)

  if (entries.length === 0) {
    // eslint-disable-next-line no-console
    console.log('Prompt Body:', truncateContent(body, MESSAGE_HISTORY_MAX_TOTAL_LENGTH))
    return
  }

  const countLabel =
    totalEntryCount > entries.length
      ? `${entries.length} most recent of ${totalEntryCount} messages`
      : `${entries.length} messages`

  // eslint-disable-next-line no-console
  console.groupCollapsed(`Prompt Body: Message History (${countLabel})`)

  const perEntryLengthLimit = getPerEntryLengthLimit(entries)

  for (const entry of entries) {
    const content = truncateContent(body.slice(entry.contentStart, entry.contentEnd), perEntryLengthLimit)
    const preview = getMessagePreview(content)
    const label = preview ? `Prompt Body: [${entry.role}] ${preview}` : `Prompt Body: [${entry.role}]`

    // eslint-disable-next-line no-console
    console.groupCollapsed(label)
    // eslint-disable-next-line no-console
    console.log(content)
    // eslint-disable-next-line no-console
    console.groupEnd()
  }

  // eslint-disable-next-line no-console
  console.groupEnd()
}
